import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import {
    InstagramProfileForOnboarding,
    SpotifyArtistSearchResponse,
    TikTokProfileForOnboarding
} from 'modules/Types'
import { MarketingSelectorOption } from '../pages/AddArtist/Components/NewOnboarding/MarketingSelectorVertical'
import { track } from 'analytics'
import { CreateBrandPayload } from 'api/brandApi'
import * as Sentry from '@sentry/react'
import { MarketingGoalType, OnboardingTab } from 'modules/Const'
import useCurrentUser from 'Hooks/useCurrentUser'
import useCurrentBrand from 'Hooks/useCurrentBrand'
import { History } from 'history'
import { OnboardingAccountTypes, ONBOARDING_VERSION } from 'modules/Const'

interface OnboardingState {
    selectedOnboardingTab: string
    loadingContinueButton: boolean
    showGatheringFanbaseLoader: boolean
    selectedSpotifyArtist: SpotifyArtistSearchResponse | null
    selectedTikTokProfile: TikTokProfileForOnboarding | null
    selectedInstagramProfile: InstagramProfileForOnboarding | null
    signupWithoutProfileName: string | null
    currentAccountType: OnboardingAccountTypes | null
    marketingExperience: MarketingSelectorOption | null
    teamsTotalCreators: { value: number; label: string } | null
    marketingBudget: { value: number; label: string } | null
    marketingGoals: MarketingGoalType[] | null

    isFirstTimeOnboarding: () => boolean

    setSelectedOnboardingTab: (tab: string) => void
    setLoadingContinueButton: (loading: boolean) => void
    setShowGatheringFanbaseLoader: (loading: boolean) => void
    setSelectedSpotifyArtist: (artist: SpotifyArtistSearchResponse | null) => void
    setSelectedTikTokProfile: (profile: TikTokProfileForOnboarding | null) => void
    setSelectedInstagramProfile: (profile: InstagramProfileForOnboarding | null) => void
    setSignupWithoutProfileName: (name: string | null) => void
    setCurrentAccountType: (type: OnboardingAccountTypes | null) => void
    setMarketingExperience: (experience: MarketingSelectorOption | null) => void
    setTeamsTotalCreators: (creators: { value: number; label: string } | null) => void
    setMarketingBudget: (budget: { value: number; label: string } | null) => void
    setMarketingGoals: (goals: MarketingGoalType[] | null) => void

    resetProfileSelectionState: () => void
    trackStepCompleted: (stepIndex: number, stepName: string) => void
    handleGoNext: (accountType?: OnboardingAccountTypes) => Promise<void>
    handleGoBack: (history: History) => void
    goToDashboard: (history: History) => void
    showBackButton: () => boolean
    showBackButtonText: () => string
    trackOnboardingEvent: (eventName: string, eventData?: Record<string, any>) => void
    createBrandOnboarding: () => Promise<void>
}

const useOnboarding = create(
    immer<OnboardingState>((set, get) => ({
        // State
        selectedOnboardingTab: 'account_type',
        loadingContinueButton: false,
        showGatheringFanbaseLoader: false,
        selectedSpotifyArtist: null,
        selectedTikTokProfile: null,
        selectedInstagramProfile: null,
        signupWithoutProfileName: null,
        currentAccountType: null,
        marketingExperience: null,
        teamsTotalCreators: null,
        marketingBudget: null,
        marketingGoals: null,

        isFirstTimeOnboarding: () => {
            // NOTE: if the user does not have onboarding answers present, then it is their first time onboarding
            const { currentUser } = useCurrentUser.getState()
            return !Boolean(currentUser?.metadata?.onboarding_answers?.account_type)
        },

        // Actions
        setSelectedOnboardingTab: (tab) => set((state) => { state.selectedOnboardingTab = tab }),
        setLoadingContinueButton: (loading) => set((state) => { state.loadingContinueButton = loading }),
        setShowGatheringFanbaseLoader: (loading) => set((state) => { state.showGatheringFanbaseLoader = loading }),
        setSelectedSpotifyArtist: (artist) => set((state) => { state.selectedSpotifyArtist = artist }),
        setSelectedTikTokProfile: (profile) => set((state) => { state.selectedTikTokProfile = profile }),
        setSelectedInstagramProfile: (profile) => set((state) => { state.selectedInstagramProfile = profile }),
        setSignupWithoutProfileName: (name) => set((state) => { state.signupWithoutProfileName = name }),
        setCurrentAccountType: (type) => set((state) => { state.currentAccountType = type }),
        setMarketingExperience: (experience) => set((state) => { state.marketingExperience = experience }),
        setTeamsTotalCreators: (creators) => set((state) => { state.teamsTotalCreators = creators }),
        setMarketingBudget: (budget) => set((state) => { state.marketingBudget = budget }),
        setMarketingGoals: (goals) => set((state) => { state.marketingGoals = goals }),

        resetProfileSelectionState: () => set((state) => {
            state.selectedSpotifyArtist = null
            state.selectedTikTokProfile = null
            state.selectedInstagramProfile = null
            state.signupWithoutProfileName = null
        }),

        trackOnboardingEvent: (eventName, eventData) => {
            track(eventName, {
                ...(eventData || {}),
                version: ONBOARDING_VERSION
            })
        },

        trackStepCompleted: (stepIndex: number, stepName: string) => {
            const { currentAccountType } = get()
            track(`NEW ONBOARDING: STEP ${stepIndex} - COMPLETED`, {
                step: stepName,
                accountType: currentAccountType,
                version: ONBOARDING_VERSION
            })
        },

        handleGoNext: async (accountType?: OnboardingAccountTypes) => {
            const {
                selectedOnboardingTab,
                isFirstTimeOnboarding,
                trackStepCompleted,
                createBrandOnboarding
            } = get()
            const isFirstTimeOnboardingLocal = isFirstTimeOnboarding()

            if (isFirstTimeOnboardingLocal) {
                switch (selectedOnboardingTab) {
                    case OnboardingTab.ACCOUNT_TYPE:
                        trackStepCompleted(1, 'creator or team')
                        set(state => { state.selectedOnboardingTab = OnboardingTab.BASIC })
                        break
                    case OnboardingTab.BASIC:
                        trackStepCompleted(2, 'select a profile')
                        // Create the brand and go directly to welcome
                        await createBrandOnboarding()
                        track('NEW ONBOARDING: Flow Finished', { version: ONBOARDING_VERSION })
                        set(state => { state.selectedOnboardingTab = OnboardingTab.WELCOME })
                        break
                }
            } else {
                if (selectedOnboardingTab === OnboardingTab.ACCOUNT_TYPE) {
                    set(state => { state.selectedOnboardingTab = OnboardingTab.BASIC })
                } else if (selectedOnboardingTab === OnboardingTab.BASIC) {
                    await createBrandOnboarding()
                    set(state => { state.selectedOnboardingTab = OnboardingTab.WELCOME })
                }
            }
        },

        handleGoBack: (history: History) => {
            const {
                selectedOnboardingTab,
                isFirstTimeOnboarding,
                goToDashboard
            } = get()
            const { loadedBrands } = useCurrentBrand.getState()

            switch (selectedOnboardingTab) {
                case OnboardingTab.ACCOUNT_TYPE:
                    if (loadedBrands && loadedBrands.length > 0) {
                        goToDashboard(history)
                    }
                    // NOTE: else there is no back button
                    break
                case OnboardingTab.BASIC:
                    if (isFirstTimeOnboarding()) {
                        set(state => { state.selectedOnboardingTab = OnboardingTab.ACCOUNT_TYPE })
                    } else {
                        goToDashboard(history)
                    }
                    break
                case OnboardingTab.WELCOME:
                    set(state => { state.selectedOnboardingTab = OnboardingTab.BASIC })
                    break
                default:
                    goToDashboard(history)
                    break
            }
        },

        goToDashboard: (history) => {
            const { currentUser } = useCurrentUser.getState()
            const { currentAccountType, marketingExperience, marketingBudget, marketingGoals, teamsTotalCreators } = get()
            const onboardingAnswers = {
                account_type: currentAccountType ?? undefined,
                marketing_experience: marketingExperience?.title ?? undefined,
                marketing_budget: marketingBudget?.value ?? undefined,
                marketing_budget_identifier: marketingBudget?.label ?? undefined,
                marketing_goals: marketingGoals && marketingGoals?.length > 0 ? marketingGoals.map((goal) => goal.type) : null,
                teams_total_creators: teamsTotalCreators?.value || undefined,
                teams_total_creators_identifier: teamsTotalCreators?.label || undefined,
            }

            track('Finalized Artist Creation Flow - Going to Dashboard', {
                newOnboarding: !Boolean(currentUser?.metadata?.onboarding_answers?.account_type),
                ...onboardingAnswers,
                version: ONBOARDING_VERSION
            })

            history.push('dashboard')
        },

        showBackButton: () => {
            const { loadedBrands } = useCurrentBrand.getState()
            const { selectedOnboardingTab: selectedTab } = get()
            switch (selectedTab) {
                case OnboardingTab.ACCOUNT_TYPE: return loadedBrands!.length > 0
                case OnboardingTab.BASIC: return true
                case OnboardingTab.WELCOME: return true
                default: return false
            }
        },

        showBackButtonText: () => {
            const { loadedBrands } = useCurrentBrand.getState()
            const { selectedOnboardingTab: selectedTab } = get()
            if (selectedTab === OnboardingTab.ACCOUNT_TYPE && loadedBrands!.length > 0) {
                return 'Back to dashboard'
            }
            return ''
        },

        createBrandOnboarding: async () => {
            const { createBrand, setCurrentBrand } = useCurrentBrand.getState()
            const {
                currentAccountType,
                marketingExperience,
                marketingBudget,
                marketingGoals,
                teamsTotalCreators,
                selectedSpotifyArtist,
                selectedTikTokProfile,
                selectedInstagramProfile,
                signupWithoutProfileName
            } = get()
            try {
                const onboardingAnswers = {
                    account_type: currentAccountType ?? undefined,
                    marketing_experience: marketingExperience?.title ?? undefined,
                    marketing_budget: marketingBudget?.value ?? undefined,
                    marketing_goals: marketingGoals && marketingGoals?.length > 0 ? marketingGoals.map((goal) => goal.type) : null,
                    teams_total_creators: teamsTotalCreators?.value || undefined,
                    teams_total_creators_identifier: teamsTotalCreators?.label || undefined,
                }

                track('NEW ONBOARDING: CREATING BRAND', {
                    ...onboardingAnswers,
                    version: ONBOARDING_VERSION
                })

                set(state => { state.showGatheringFanbaseLoader = true })

                const brandName = selectedSpotifyArtist?.name ||
                    selectedTikTokProfile?.username ||
                    selectedInstagramProfile?.username ||
                    signupWithoutProfileName ||
                    null

                const customization: CreateBrandPayload['customization'] = {
                    onboarding_answers: onboardingAnswers,
                    spotify_artist: selectedSpotifyArtist || undefined,
                    tiktok: selectedTikTokProfile || undefined,
                    instagram_profile_for_onboarding: selectedInstagramProfile || undefined
                }

                const payload: CreateBrandPayload = {
                    name: brandName,
                    customization
                }

                const createdBrand = await createBrand(payload)

                track('Finished Artist Creation', {
                    ...onboardingAnswers,
                    version: ONBOARDING_VERSION
                })

                setCurrentBrand(createdBrand)

                // Reset state after successful creation
                set(state => {
                    state.selectedSpotifyArtist = null
                    state.selectedTikTokProfile = null
                    state.selectedInstagramProfile = null
                    state.signupWithoutProfileName = null
                    state.showGatheringFanbaseLoader = false
                })

            } catch (e) {
                Sentry.captureException(e)
                console.log('error', e)
                set(state => { state.showGatheringFanbaseLoader = false })
            }
        }
    }))
);

export default useOnboarding
